/**
 * Gift Reggie wishlist guard
 * ---------------------------------------------------------------------------
 * The Gift Reggie heart (.giftreggie-pdp-wishlist-cta) is injected by the app
 * and silently fails when the shopper is logged out or has no wishlist yet
 * (POST /add -> 400, GET /wishlist -> 404, error swallowed, no UI feedback).
 * There is no app-embed setting for this, so we intercept the click in the
 * capture phase -- before the app's failing handler -- and give the heart a
 * real behavior based on shopper state. Shoppers who already have a wishlist
 * are never intercepted; the app's normal add runs untouched.
 *
 * Config is provided by layout/theme.liquid as window.giftReggieGuard.
 */
(function () {
  'use strict';

  var cfg = window.giftReggieGuard;
  if (!cfg) return;

  var HEART_SELECTOR = '.giftreggie-pdp-wishlist-cta';

  // null = unknown/pending, true/false once the load-time GET resolves.
  var hasWishlist = null;
  var wishlistReady;

  if (cfg.loggedIn && cfg.apiUrl) {
    wishlistReady = fetch(cfg.apiUrl, { credentials: 'same-origin' })
      .then(function (res) {
        hasWishlist = res.ok; // 200 -> has a list; 404/other -> none
      })
      .catch(function () {
        hasWishlist = false; // network error -> safe branch
      });
  } else {
    // Logged out: never need the lookup.
    hasWishlist = false;
    wishlistReady = Promise.resolve();
  }

  function withParam(url, key, value) {
    var sep = url.indexOf('?') > -1 ? '&' : '?';
    return url + sep + key + '=' + encodeURIComponent(value);
  }

  function goToLogin() {
    var returnTo = window.location.pathname + window.location.search;
    window.location.href = withParam(cfg.loginUrl, 'return_url', returnTo);
  }

  var AUTO_DISMISS_MS = 7000;
  var toastEl, toastText, dismissTimer;

  function buildToast() {
    toastEl = document.createElement('div');
    toastEl.className = 'gr-wishlist-toast';
    toastEl.setAttribute('role', 'status');

    // The message itself is the call to action: clicking it creates the list.
    var cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'gr-wishlist-toast__cta';
    cta.addEventListener('click', function () {
      window.location.href = cfg.wishlistUrl;
    });

    toastText = document.createElement('span');
    toastText.className = 'gr-wishlist-toast__text';
    cta.appendChild(toastText);

    var arrow = document.createElement('span');
    arrow.className = 'gr-wishlist-toast__arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '\u2192'; // right arrow (JS escape, never touches Liquid/JSON)
    cta.appendChild(arrow);

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'gr-wishlist-toast__close';
    close.setAttribute('aria-label', (cfg.strings && cfg.strings.dismiss) || 'Close');
    close.textContent = '\u00d7'; // multiplication sign as close glyph
    close.addEventListener('click', hideToast);

    toastEl.appendChild(cta);
    toastEl.appendChild(close);
    document.body.appendChild(toastEl);
  }

  function showWishlistPrompt() {
    if (!toastEl) buildToast();
    toastText.textContent = cfg.strings.createPrompt;
    // Force reflow so the enter transition runs on a freshly-shown element.
    void toastEl.offsetWidth;
    toastEl.classList.add('gr-wishlist-toast--visible');
    window.clearTimeout(dismissTimer);
    dismissTimer = window.setTimeout(hideToast, AUTO_DISMISS_MS);
  }

  function hideToast() {
    window.clearTimeout(dismissTimer);
    if (toastEl) toastEl.classList.remove('gr-wishlist-toast--visible');
  }

  // Never let the toast linger when the shopper returns via Back/Forward:
  // hide before unloading (so the bfcache snapshot is clean) and again on
  // restore (belt and suspenders for browsers that snapshot regardless).
  window.addEventListener('pagehide', hideToast);
  window.addEventListener('pageshow', hideToast);

  function handleHeart(heart, event) {
    // Logged out -> send to login, come back to this PDP.
    if (!cfg.loggedIn) {
      event.preventDefault();
      event.stopImmediatePropagation();
      goToLogin();
      return;
    }

    // Known to have a wishlist -> let the app's own handler run.
    if (hasWishlist === true) return;

    // Known to have none -> block the doomed click and prompt to create.
    if (hasWishlist === false) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showWishlistPrompt();
      return;
    }

    // State still pending (very fast click). Block now, decide once resolved.
    event.preventDefault();
    event.stopImmediatePropagation();
    wishlistReady.then(function () {
      if (hasWishlist) {
        // They do have a list -> replay the click so the app handles it.
        heart.setAttribute('data-gr-bypass', '1');
        heart.click();
        heart.removeAttribute('data-gr-bypass');
      } else {
        showWishlistPrompt();
      }
    });
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var heart = target.closest(HEART_SELECTOR);
      if (!heart) return;
      // Replayed click -> pass straight through to the app's handler.
      if (heart.hasAttribute('data-gr-bypass')) return;
      handleHeart(heart, event);
    },
    true // capture: run before the app's button-bound handler
  );
})();
